/**
 * Générateur — Identifier la structure d'un motif évolutif
 * Attendu ID : 638 | Niveau : 6ème | Domaine : A (Algèbre)
 *
 * Ce générateur contient 2 EXERCICES DIDACTIQUEMENT DISTINCTS :
 *
 *   1. `suite_additive`       → QCM, Saisie, Texte à trous.
 *      Suites additives : valeur initiale + (n−1) × incrément.
 *
 *   2. `suite_multiplicative`  → QCM, Saisie, Texte à trous.
 *      Suites multiplicatives : valeur initiale × facteur^(n−1).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 638;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ADDITIVE = 'Compléter une suite additive et calculer un terme éloigné';
const L_MULTIPLI = 'Compléter une suite multiplicative et calculer un terme éloigné';

// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

/** Ajoute un distracteur unique et différent de la bonne réponse. */
const addUnique = (set, val, correct) => {
    if (val !== correct && val > 0 && Number.isInteger(val)) set.add(val);
};

/** Élision « de » → « d' » devant voyelle ou h muet. */
const deElide = (mot) => {
    const first = mot.normalize('NFD').replace(/[\u0300-\u036f]/g, '')[0].toLowerCase();
    return /[aeiouh]/.test(first) ? `d'${mot}` : `de ${mot}`;
};

// ═══════════════════════════════════════════════════════════
// SVG — Illustrations allumettes / carrelages
// ═══════════════════════════════════════════════════════════

const COL_MATCH = '#f59e0b';   // allumette (amber-500)
const COL_MATCH2 = '#d97706';  // allumette foncée (amber-600)
const COL_SHARE = '#22c55e';   // mur partagé (green-500)
const COL_ADD = '#fb923c';     // +N label (orange-400)
const COL_TEXT = 'currentColor';    // texte — s'adapte au thème via CSS
const COL_TILE = '#3b82f6';    // carreau (blue-500)
const COL_TILE2 = '#2563eb';   // carreau stroke (blue-600)

/** Maison : carré + toit en V. Retourne les éléments SVG pour une maison à la position x. */
const svgMaison = (x, y, w, h, color, isFirst) => {
    const peak = y - h - h * 0.5;
    const mid = x + w / 2;
    const parts = [];
    // Mur gauche (sauf si pas la première — c'est le mur partagé)
    if (isFirst) {
        parts.push(`<line x1="${x}" y1="${y}" x2="${x}" y2="${y - h}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
        parts.push(`<circle cx="${x}" cy="${y - h}" r="3" fill="${color}"/>`);
    }
    // Mur droit
    parts.push(`<line x1="${x + w}" y1="${y}" x2="${x + w}" y2="${y - h}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    parts.push(`<circle cx="${x + w}" cy="${y - h}" r="3" fill="${color}"/>`);
    // Sol + plafond
    parts.push(`<line x1="${x}" y1="${y}" x2="${x + w}" y2="${y}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    parts.push(`<line x1="${x}" y1="${y - h}" x2="${x + w}" y2="${y - h}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    // Toit
    parts.push(`<line x1="${x}" y1="${y - h}" x2="${mid}" y2="${peak}" stroke="${COL_MATCH2}" stroke-width="3" stroke-linecap="round"/>`);
    parts.push(`<circle cx="${mid}" cy="${peak}" r="3" fill="${COL_MATCH2}"/>`);
    parts.push(`<line x1="${x + w}" y1="${y - h}" x2="${mid}" y2="${peak}" stroke="${COL_MATCH2}" stroke-width="3" stroke-linecap="round"/>`);
    return parts.join('');
};

/** Mur partagé vert entre deux maisons/carrés. */
const svgSharedWall = (x, yBot, yTop) =>
    `<line x1="${x}" y1="${yBot}" x2="${x}" y2="${yTop}" stroke="${COL_SHARE}" stroke-width="3.5" stroke-linecap="round"/>` +
    `<circle cx="${x}" cy="${yTop}" r="3" fill="${COL_SHARE}"/>`;

/** Carré simple (frise). */
const svgCarre = (x, y, s, color, isFirst) => {
    const parts = [];
    if (isFirst) {
        parts.push(`<line x1="${x}" y1="${y}" x2="${x}" y2="${y - s}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    }
    parts.push(`<line x1="${x + s}" y1="${y}" x2="${x + s}" y2="${y - s}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    parts.push(`<line x1="${x}" y1="${y}" x2="${x + s}" y2="${y}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    parts.push(`<line x1="${x}" y1="${y - s}" x2="${x + s}" y2="${y - s}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    return parts.join('');
};

/** Triangle pointe en haut. x = coin bas-gauche. Côté partagé = oblique gauche (skippé si !isFirst). */
const svgTriangleUp = (x, y, base, h, color, isFirst) => {
    const parts = [];
    // Oblique gauche (côté partagé avec le triangle inversé précédent)
    if (isFirst) {
        parts.push(`<line x1="${x}" y1="${y}" x2="${x + base / 2}" y2="${y - h}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    }
    // Oblique droite
    parts.push(`<line x1="${x + base}" y1="${y}" x2="${x + base / 2}" y2="${y - h}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    // Base (bas)
    parts.push(`<line x1="${x}" y1="${y}" x2="${x + base}" y2="${y}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    parts.push(`<circle cx="${x + base / 2}" cy="${y - h}" r="3" fill="${color}"/>`);
    return parts.join('');
};

/** Triangle pointe en bas. x = coin haut-gauche. Côté partagé = oblique gauche (skippé). */
const svgTriangleDown = (x, y, base, h, color) => {
    const parts = [];
    // Base (haut)
    parts.push(`<line x1="${x}" y1="${y}" x2="${x + base}" y2="${y}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    // Oblique droite
    parts.push(`<line x1="${x + base}" y1="${y}" x2="${x + base / 2}" y2="${y + h}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    // Pointe
    parts.push(`<circle cx="${x + base / 2}" cy="${y + h}" r="3" fill="${color}"/>`);
    return parts.join('');
};

/** Hexagone pointy-top (frise). cx, cy = centre. s = longueur d'un côté.
 *  Côté partagé = côté vertical gauche (segment 3→4), skippé si !isFirst. */
const svgHexagone = (cx, cy, s, color, isFirst) => {
    const w = s * Math.sqrt(3) / 2; // demi-largeur
    // Sommets pointy-top (sens horaire depuis le sommet haut)
    const pts = [
        [cx,     cy - s],      // 0: top
        [cx + w, cy - s / 2],  // 1: top-right
        [cx + w, cy + s / 2],  // 2: bottom-right
        [cx,     cy + s],      // 3: bottom
        [cx - w, cy + s / 2],  // 4: bottom-left
        [cx - w, cy - s / 2],  // 5: top-left
    ];
    // Côtés : 0→1, 1→2 (vertical droit), 2→3, 3→4, 4→5 (vertical gauche), 5→0
    // Skip côté 4→5 (vertical gauche) si pas la première → c'est le côté partagé
    const parts = [];
    for (let i = 0; i < 6; i++) {
        if (!isFirst && i === 4) continue;
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % 6];
        parts.push(`<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`);
    }
    return parts.join('');
};

/** Carreau plein (carrelage). */
const svgCarreau = (x, y, s) =>
    `<rect x="${x}" y="${y}" width="${s}" height="${s}" rx="2" fill="${COL_TILE}" stroke="${COL_TILE2}" stroke-width="1"/>`;

/** Construit le SVG complet d'une frise pour un contexte donné. Montre 3 motifs + "..." + question.
 *  CONTRAINTE PRINT : cette image est aussi imprimée, souvent en demi-colonne
 *  (~85 mm). Le viewBox colle au contenu réel (toute bande vide réduit
 *  l'échelle de rendu, donc la taille apparente des textes) et la typo reste
 *  ≥ 16px — en dessous, les libellés deviennent illisibles une fois réduits. */
const buildFriseSvg = (ctxId, rang) => {
    const FONT = 'system-ui,sans-serif';
    const FS_LABEL = 16;     // libellés sous/à côté des motifs
    const FS_QUESTION = 17;  // « N motifs ? »
    const baseY = 150;

    let content = '';
    let xEnd = 0;               // bord droit du contenu dessiné
    let W = 0, H = 185;
    let questionInline = true;  // question à droite des motifs (sauf carrelages)

    if (ctxId === 'allumettes_maisons') {
        const motifW = 80;
        for (let i = 0; i < 3; i++) {
            const x = 20 + i * motifW;
            content += svgMaison(x, baseY, motifW, 70, i === 0 ? COL_MATCH : COL_ADD, i === 0);
            if (i > 0) content += svgSharedWall(x, baseY, baseY - 70);
            const label = i === 0 ? `${6} allum.` : `+ 5`;
            const col = i === 0 ? COL_TEXT : COL_ADD;
            content += `<text x="${x + motifW / 2}" y="${baseY + 24}" font-size="${FS_LABEL}" fill="${col}" text-anchor="middle" font-family="${FONT}">${label}</text>`;
        }
        xEnd = 20 + 3 * motifW;
    } else if (ctxId === 'allumettes_carres') {
        const s = 60;
        for (let i = 0; i < 3; i++) {
            const x = 20 + i * s;
            content += svgCarre(x, baseY, s, i === 0 ? COL_MATCH : COL_ADD, i === 0);
            if (i > 0) content += svgSharedWall(x, baseY, baseY - s);
            const label = i === 0 ? `${4} allum.` : `+ 3`;
            const col = i === 0 ? COL_TEXT : COL_ADD;
            content += `<text x="${x + s / 2}" y="${baseY + 24}" font-size="${FS_LABEL}" fill="${col}" text-anchor="middle" font-family="${FONT}">${label}</text>`;
        }
        xEnd = 20 + 3 * s;
    } else if (ctxId === 'allumettes_triangles') {
        // halfBase 45 (vs 35 avant) : espace les labels 16px sans collision
        const halfBase = 45, h = 70;
        const topY = baseY - h;
        // 5 triangles alternés : △▽△▽△ (3 "rangs" de la suite : 3, +2, +2)
        // Chaque triangle occupe halfBase de large. Les triangles partagent un côté oblique.
        for (let i = 0; i < 5; i++) {
            const x = 20 + i * halfBase;
            const color = i === 0 ? COL_MATCH : COL_ADD;
            if (i % 2 === 0) {
                // Triangle pointe en haut
                content += svgTriangleUp(x, baseY, halfBase * 2, h, color, i === 0);
            } else {
                // Triangle pointe en bas — côté oblique gauche partagé (vert)
                content += svgTriangleDown(x, topY, halfBase * 2, h, color);
                content += `<line x1="${x}" y1="${topY}" x2="${x + halfBase}" y2="${baseY}" stroke="${COL_SHARE}" stroke-width="3.5" stroke-linecap="round"/>`;
            }
        }
        // Labels sous les 3 premiers motifs (rang 1 = △, rang 2 = ▽, rang 3 = △)
        const labels = [`${3} allum.`, `+ 2`, `+ 2`];
        const cols = [COL_TEXT, COL_ADD, COL_ADD];
        const positions = [20 + halfBase, 20 + 2 * halfBase, 20 + 3 * halfBase];
        for (let j = 0; j < 3; j++) {
            content += `<text x="${positions[j]}" y="${baseY + 24}" font-size="${FS_LABEL}" fill="${cols[j]}" text-anchor="middle" font-family="${FONT}">${labels[j]}</text>`;
        }
        xEnd = 20 + 6 * halfBase; // dernier triangle : x = 20 + 4·hb, base = 2·hb
    } else if (ctxId === 'allumettes_hexagones') {
        const s = 34; // vs 24 avant : labels 16px sans chevauchement
        const w = s * Math.sqrt(3) / 2; // demi-largeur d'un hex pointy-top
        const step = 2 * w; // décalage entre centres = largeur complète (côté vertical partagé)
        const cy = baseY - s; // bas des hexagones posé sur la ligne de base
        for (let i = 0; i < 3; i++) {
            const cx = 20 + w + i * step;
            content += svgHexagone(cx, cy, s, i === 0 ? COL_MATCH : COL_ADD, i === 0);
            // Mur partagé vert (côté vertical gauche de l'hex suivant)
            if (i > 0) {
                content += `<line x1="${(cx - w).toFixed(1)}" y1="${(cy - s / 2).toFixed(1)}" x2="${(cx - w).toFixed(1)}" y2="${(cy + s / 2).toFixed(1)}" stroke="${COL_SHARE}" stroke-width="3.5" stroke-linecap="round"/>`;
            }
            const label = i === 0 ? `${6} allum.` : `+ 5`;
            const col = i === 0 ? COL_TEXT : COL_ADD;
            content += `<text x="${cx}" y="${baseY + 24}" font-size="${FS_LABEL}" fill="${col}" text-anchor="middle" font-family="${FONT}">${label}</text>`;
        }
        xEnd = 20 + 2 * w + 2 * step;
    } else if (ctxId === 'carrelages') {
        // Rangées EMPILÉES (avant : 5+9+13 carreaux en ligne unique → SVG de
        // ~680px de large qui rétrécissait tout au rendu). Labels à droite.
        const s = 22;
        const motifs = [5, 9, 13];
        const labels = [`${5} carr.`, `+ 4`, `+ 4`];
        const labelX = 20 + 13 * s + 14;
        for (let m = 0; m < 3; m++) {
            const y = 28 + m * 32;
            for (let j = 0; j < motifs[m]; j++) {
                content += svgCarreau(20 + j * s, y, s - 2);
            }
            const col = m === 0 ? COL_TEXT : COL_ADD;
            content += `<text x="${labelX}" y="${y + 16}" font-size="${FS_LABEL}" fill="${col}" font-family="${FONT}">${labels[m]}</text>`;
        }
        // Question sous les rangées
        content += `<text x="20" y="160" font-size="24" fill="#64748b" font-family="${FONT}">…</text>`;
        content += `<text x="52" y="158" font-size="${FS_QUESTION}" fill="${COL_TEXT}" font-family="${FONT}">${rang} motifs ?</text>`;
        questionInline = false;
        W = labelX + 70 + 10; // rangées + libellé « 5 carr. » à droite
        H = 172;
    }

    // Points de suspension + question, dans la continuité des motifs
    if (questionInline) {
        const qx = xEnd + 24;
        content += `<text x="${qx}" y="105" font-size="24" fill="#64748b" font-family="${FONT}">…</text>`;
        content += `<text x="${qx + 32}" y="103" font-size="${FS_QUESTION}" fill="${COL_TEXT}" font-family="${FONT}">${rang} motifs ?</text>`;
        W = qx + 32 + 112; // ~112px pour « 50 motifs ? » en 17px
    }

    // Légende mur partagé (allumettes) — en haut à gauche, zone toujours libre
    if (ctxId.startsWith('allumettes')) {
        content += `<line x1="20" y1="16" x2="44" y2="16" stroke="${COL_SHARE}" stroke-width="3" stroke-linecap="round"/>`;
        content += `<text x="52" y="21" font-size="13" fill="${COL_SHARE}" font-family="${FONT}">mur partagé</text>`;
    }

    const svg = `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${content}</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 1 — suite_additive
// ═══════════════════════════════════════════════════════════

/**
 * Contextes pour suites additives.
 * Chaque contexte a une valeur initiale, un incrément, et un template d'énoncé.
 */
const CTX_ADDITIF = [
    {
        id: 'allumettes_maisons',
        label: 'maisons en allumettes',
        init: 6, incr: 5,
        enonce: (rang) => `On fabrique des maisons avec des allumettes. La première maison nécessite 6 allumettes. Chaque maison supplémentaire nécessite 5 allumettes de plus (un mur est partagé). Combien faut-il d'allumettes pour réaliser ${rang} maisons ?`,
        suffixe: 'allumettes',
    },
    {
        id: 'allumettes_carres',
        label: 'carrés en allumettes',
        init: 4, incr: 3,
        enonce: (rang) => `On construit une frise de carrés avec des allumettes. Le premier carré nécessite 4 allumettes. Chaque carré supplémentaire nécessite 3 allumettes de plus. Combien faut-il d'allumettes pour ${rang} carrés ?`,
        suffixe: 'allumettes',
    },
    {
        id: 'allumettes_triangles',
        label: 'triangles en allumettes',
        init: 3, incr: 2,
        enonce: (rang) => `On construit une frise de triangles avec des allumettes. Le premier triangle nécessite 3 allumettes. Chaque triangle supplémentaire nécessite 2 allumettes de plus. Combien faut-il d'allumettes pour ${rang} triangles ?`,
        suffixe: 'allumettes',
    },
    {
        id: 'allumettes_hexagones',
        label: 'hexagones en allumettes',
        init: 6, incr: 5,
        enonce: (rang) => `On construit une frise d'hexagones avec des allumettes. Le premier hexagone nécessite 6 allumettes. Chaque hexagone supplémentaire nécessite 5 allumettes de plus. Combien faut-il d'allumettes pour ${rang} hexagones ?`,
        suffixe: 'allumettes',
    },
    {
        id: 'carrelages',
        label: 'carrelages',
        init: 5, incr: 4,
        enonce: (rang) => `On pose des carrelages en ligne. Le premier motif utilise 5 carreaux. Chaque motif supplémentaire nécessite 4 carreaux de plus. Combien faut-il de carreaux pour ${rang} motifs ?`,
        suffixe: 'carreaux',
    },
];

/**
 * Génère les données d'une suite additive.
 * Mode 'numerique' : suite de nombres pures.
 * Mode 'contexte' : suite en contexte géométrique.
 */
const genAdditiveData = () => {
    const useContexte = Math.random() < 0.5;

    if (useContexte) {
        const ctx = randomChoice(CTX_ADDITIF);
        // Rang continu (revue 2026-08-01) : la liste fermée de 7 rangs ne
        // laissait que 35 énoncés contexte possibles (doublons fréquents en
        // lot de 40). Le rang est le seul paramètre libre du mode contexte.
        const rang = randInt(5, 60);
        const terme = ctx.init + (rang - 1) * ctx.incr;
        return { mode: 'contexte', ctx, init: ctx.init, incr: ctx.incr, rang, terme };
    }

    // Suite numérique pure — pool dynamique large (~800 combos)
    const init = randomChoice([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12]);
    const incr = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 10]);
    const rang = randomChoice([8, 10, 12, 15, 20, 25, 30, 50]);
    const terme = init + (rang - 1) * incr;
    // Limiter à 1000
    if (terme > 1000) return genAdditiveData();
    return { mode: 'numerique', init, incr, rang, terme };
};

/** Construit l'énoncé texte + éventuellement l'image SVG pour une suite additive.
 *  Retourne { texte, elAdd } où elAdd est un elements_additionnels ou null. */
const buildAdditiveTexte = (data, question) => {
    if (data.mode === 'contexte') {
        const svgUrl = buildFriseSvg(data.ctx.id, data.rang);
        const texte = `<p>${data.ctx.enonce(data.rang)}</p>{{element:0}}`;
        const elAdd = { version: 1, elements: [{ type: 'image', config: { imageUrl: svgUrl } }] };
        return { texte, elAdd };
    }
    const terms = [];
    for (let i = 0; i < 4; i++) terms.push(data.init + i * data.incr);
    return { texte: `<p>Voici une suite de nombres : ${terms.join(' ; ')} ; …<br>À chaque étape, on ajoute le même nombre. ${question}</p>`, elAdd: null };
};

// --- QCM ---
const genAdditiveQcm = () => {
    const data = genAdditiveData();
    const { init, incr, rang, terme } = data;
    const correct = terme;

    // Anti « élimination des extrêmes » (revue 2026-08-01) : les deux pièges de
    // modèle (rang × incr / init × rang) ENCADRENT mécaniquement le terme selon
    // le signe de init − incr → la bonne réponse n'était jamais max ni min
    // (barrer les extrêmes donnait 50 % sans calcul). Une part des tirages sert
    // donc les trois distracteurs SOUS le terme : la bonne réponse devient le max.
    // p = 1/3 (calibré revue 2026-08-02) : point d'équilibre où « cocher le
    // max » et « barrer les extrêmes » plafonnent toutes deux à ~33 %.
    const dSet = new Set();
    if (Math.random() < 1 / 3) {
        // Tous les distracteurs en dessous du terme
        addUnique(dSet, init > incr ? rang * incr : init * rang, correct); // piège de modèle côté bas
        addUnique(dSet, terme - incr, correct);                            // 1 cran en dessous
        addUnique(dSet, init + incr, correct);                             // 2e terme seulement
        addUnique(dSet, terme - 2 * incr, correct);                        // secours, toujours < terme
    } else {
        // Construction classique : les deux pièges de modèle + un proche ±
        addUnique(dSet, rang * incr, correct);                   // oubli valeur initiale
        addUnique(dSet, init * rang, correct);                   // multiplier par rang
        const proche = Math.random() < 0.5 ? terme + incr : terme - incr;
        addUnique(dSet, proche, correct);                        // ± 1 cran, jamais systématique
        addUnique(dSet, init + incr, correct);                   // 2e terme seulement
    }
    let att = 0;
    while (dSet.size < 3 && att < 20) {
        addUnique(dSet, terme + randInt(-20, 20), correct);
        att++;
    }
    const choixList = [String(correct), ...[...dSet].slice(0, 3).map(String)];

    const question = `Quel est le ${rang}<sup>e</sup> terme de cette suite ?`;
    const { texte, elAdd } = buildAdditiveTexte(data, question);

    const payload = makeQcm(choixList, String(correct), texte, { ...opts(L_ADDITIVE), ...(elAdd ? { elements_additionnels: elAdd } : {}) });
    payload._meta.generatorKey = 'suite_additive';
    return payload;
};

// --- Saisie ---
const genAdditiveSaisie = () => {
    const data = genAdditiveData();
    const { terme } = data;

    if (data.mode === 'contexte') {
        const svgUrl = buildFriseSvg(data.ctx.id, data.rang);
        const texte = `<p>${data.ctx.enonce(data.rang)}</p>{{element:0}}`;
        const elAdd = { version: 1, elements: [{ type: 'image', config: { imageUrl: svgUrl } }] };
        const payload = makeSaisie(String(terme), texte, { ...opts(L_ADDITIVE), suffixe: data.ctx.suffixe, elements_additionnels: elAdd });
        payload._meta.generatorKey = 'suite_additive';
        return payload;
    }

    // Mode numérique pur
    const terms = [];
    for (let i = 0; i < 4; i++) terms.push(data.init + i * data.incr);
    const texte = `<p>Voici une suite de nombres : ${terms.join(' ; ')} ; …<br>À chaque étape, on ajoute le même nombre. Quel est le ${data.rang}<sup>e</sup> terme de cette suite ?</p>`;
    const payload = makeSaisie(String(terme), texte, opts(L_ADDITIVE));
    payload._meta.generatorKey = 'suite_additive';
    return payload;
};

// --- Texte à trous ---
const genAdditiveTexteTrou = () => {
    const useContexte = Math.random() < 0.5;

    if (useContexte) {
        const ctx = randomChoice(CTX_ADDITIF);
        // Rang continu (revue 2026-08-02), mêmes bornes que l'ancienne liste :
        // 7 valeurs ne donnaient que 35 énoncés contexte (5 doublons par lot).
        const rang = randInt(10, 30);
        const terme = ctx.init + (rang - 1) * ctx.incr;

        const t1 = ctx.init;
        const t2 = ctx.init + ctx.incr;
        const t3 = ctx.init + 2 * ctx.incr;
        const nbAjouts = rang - 1;

        const phrase = `Rang 1 : {{${t1}}} ${ctx.suffixe}.\nRang 2 : ${t1} + ${ctx.incr} = {{${t2}}} ${ctx.suffixe}.\nRang 3 : ${t2} + ${ctx.incr} = {{${t3}}} ${ctx.suffixe}.\nÀ chaque rang, on ajoute {{${ctx.incr}}}.\nAu rang ${rang}, le nombre ${deElide(ctx.suffixe)} est ${t1} + {{${nbAjouts}}} × ${ctx.incr} = {{${terme}}}.`;

        const svgUrl = buildFriseSvg(ctx.id, rang);
        const texte = `<p>${ctx.label.charAt(0).toUpperCase() + ctx.label.slice(1)} : le premier motif utilise ${ctx.init} ${ctx.suffixe}, puis on ajoute ${ctx.incr} à chaque étape. Complète le raisonnement pour trouver le nombre ${deElide(ctx.suffixe)} au rang ${rang}.</p>{{element:0}}`;
        const elAdd = { version: 1, elements: [{ type: 'image', config: { imageUrl: svgUrl } }] };

        const payload = makeTexteTrou(phrase, texte, { ...opts(L_ADDITIVE), mode: 'saisie', strict: false, elements_additionnels: elAdd });
        payload._meta.generatorKey = 'suite_additive';
        return payload;
    }

    // Mode numérique pur
    const init = randomChoice([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12]);
    const incr = randomChoice([2, 3, 4, 5, 6, 7, 8, 9, 10]);
    const rang = randInt(10, 30);
    const terme = init + (rang - 1) * incr;
    if (terme > 1000) return genAdditiveTexteTrou();

    const t1 = init;
    const t2 = init + incr;
    const t3 = init + 2 * incr;
    const nbAjouts = rang - 1;

    const phrase = `Terme 1 : {{${t1}}}.\nTerme 2 : ${t1} + ${incr} = {{${t2}}}.\nTerme 3 : ${t2} + ${incr} = {{${t3}}}.\nÀ chaque rang, on ajoute {{${incr}}}.\nAu rang ${rang}, le terme vaut ${t1} + {{${nbAjouts}}} × ${incr} = {{${terme}}}.`;

    const texte = `<p>Voici une suite de nombres : ${t1} ; ${t2} ; ${t3} ; … À chaque étape, on ajoute le même nombre. Complète le raisonnement pour trouver le ${rang}<sup>e</sup> terme.</p>`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_ADDITIVE), mode: 'saisie', strict: false });
    payload._meta.generatorKey = 'suite_additive';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 2 — suite_multiplicative
// ═══════════════════════════════════════════════════════════

/**
 * Génère dynamiquement (init, facteur, rang) pour une suite multiplicative.
 * Contrainte : init × facteur^(rang-1) ≤ 10000, 1 ≤ init ≤ 9.
 * Échantillonne uniformément parmi TOUS les triplets valides (évite le biais
 * d'un facteur avec peu de combos).
 * rangMin/rangMax : bornes optionnelles sur le rang (pour texte_trou).
 */
const genMultiData = (rangMin = null, rangMax = null) => {
    const rangsByFacteur = {
        2: [6, 7, 8, 9, 10, 11, 12, 13],
        3: [5, 6, 7, 8],
        5: [4, 5, 6],
        10: [3, 4, 5],
    };
    const combos = [];
    for (const fStr of Object.keys(rangsByFacteur)) {
        const facteur = Number(fStr);
        for (const rang of rangsByFacteur[facteur]) {
            if (rangMin !== null && rang < rangMin) continue;
            if (rangMax !== null && rang > rangMax) continue;
            // init ≤ 20 (revue 2026-08-02, avant : 9) : ~240 triplets au lieu
            // de 131. Le plafond terme ≤ 10000 continue de borner via maxInit.
            const maxInit = Math.min(Math.floor(10000 / Math.pow(facteur, rang - 1)), 20);
            for (let init = 1; init <= maxInit; init++) {
                combos.push({ init, facteur, rang });
            }
        }
    }
    const { init, facteur, rang } = randomChoice(combos);
    const terme = init * Math.pow(facteur, rang - 1);
    return { init, facteur, rang, terme };
};

/** Mots pour le facteur. */
const motFacteur = (k) => {
    if (k === 2) return 'on multiplie par 2 (on double)';
    if (k === 3) return 'on multiplie par 3 (on triple)';
    return `on multiplie par ${k}`;
};

// --- QCM ---
const genMultiQcm = () => {
    const data = genMultiData();
    const { init, facteur, rang, terme } = data;
    const correct = terme;

    // Anti « élimination des extrêmes » (revue 2026-08-02) : « terme × facteur »
    // systématique (doublon silencieux de init × facteur^rang) et la confusion
    // additive minuscule encadraient le terme → jamais max ni min, barrer les
    // extrêmes donnait 50 % sans calcul. p = 1/3 : point d'équilibre où
    // « cocher le max » et « barrer les extrêmes » plafonnent à ~33 %.
    const dSet = new Set();
    if (Math.random() < 1 / 3) {
        // Tous les distracteurs SOUS le terme (la croissance exponentielle
        // écrase les pièges linéaires) → la bonne réponse est le max
        addUnique(dSet, init + (rang - 1) * facteur, correct);           // additif au lieu de multiplicatif
        if (Number.isInteger(terme / facteur)) {
            addUnique(dSet, terme / facteur, correct);                   // -1 cran
        }
        addUnique(dSet, init * rang * facteur, correct);                 // init × rang × facteur
        addUnique(dSet, init * facteur, correct);                        // 2e terme seulement
    } else {
        // Construction classique : pièges de modèle + « un cran de trop »
        // (au-dessus, garantit le sandwich — le ÷ facteur vit en branche basse)
        addUnique(dSet, init * rang * facteur, correct);                 // init × rang × facteur
        addUnique(dSet, init + (rang - 1) * facteur, correct);           // additif au lieu de multiplicatif
        addUnique(dSet, terme * facteur, correct);                       // +1 cran
        addUnique(dSet, init * facteur, correct);                        // 2e terme seulement
    }
    let att = 0;
    while (dSet.size < 3 && att < 30) {
        const noise = randInt(Math.max(1, Math.floor(terme * 0.3)), Math.floor(terme * 1.5));
        addUnique(dSet, noise, correct);
        att++;
    }
    const choixList = [String(correct), ...[...dSet].slice(0, 3).map(String)];

    // Afficher min(4, rang − 1) termes (revue 2026-08-02) : avec 4 termes
    // systématiques, les rangs 3 et 4 (facteurs 5 et 10, bornés par le plafond
    // de 10000) affichaient la RÉPONSE dans l'énoncé — ~21 % des tirages.
    const nbAffiches = Math.min(4, rang - 1);
    const terms = [];
    for (let i = 0; i < nbAffiches; i++) terms.push(init * Math.pow(facteur, i));

    const texte = `<p>Voici une suite de nombres : ${terms.join(' ; ')} ; …<br>Pour passer d'un terme au suivant, ${motFacteur(facteur)}. Quel est le ${rang}<sup>e</sup> terme de cette suite ?</p>`;

    const payload = makeQcm(choixList, String(correct), texte, opts(L_MULTIPLI));
    // Calculatrice AUTORISÉE (revue 2026-08-02) : jusqu'à 8 multiplications
    // successives avec retenues sur des nombres à 4 chiffres (ex. 13 → 3328).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'suite_multiplicative';
    return payload;
};

// --- Saisie ---
const genMultiSaisie = () => {
    const data = genMultiData();
    const { init, facteur, rang, terme } = data;

    // min(4, rang − 1) termes affichés (revue 2026-08-02) : comme au QCM, les
    // rangs 3-4 affichaient la réponse dans l'énoncé (~21 % des tirages).
    const nbAffiches = Math.min(4, rang - 1);
    const terms = [];
    for (let i = 0; i < nbAffiches; i++) terms.push(init * Math.pow(facteur, i));

    const texte = `<p>Voici les premiers termes d'une suite : ${terms.join(' ; ')} ; …<br>Pour passer d'un terme au suivant, ${motFacteur(facteur)}. Quel est le ${rang}<sup>e</sup> terme ?</p>`;

    const payload = makeSaisie(String(terme), texte, opts(L_MULTIPLI));
    // Calculatrice AUTORISÉE (revue 2026-08-02) : mêmes enchaînements que le
    // QCM multiplicatif, et la saisie exige le nombre exact.
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'suite_multiplicative';
    return payload;
};

// --- Texte à trous ---
const genMultiTexteTrou = () => {
    // Rang modéré pour pouvoir montrer les étapes. rangMin = 6 (revue
    // 2026-08-02, avant : 5) : au rang 5, le terme demandé était le trou
    // « Terme 5 » déjà rempli trois lignes plus haut — l'élève recopiait.
    const { init, facteur, rang, terme } = genMultiData(6, 9);

    // Construire les 5 premiers termes
    const t = [];
    for (let i = 0; i < 5; i++) t.push(init * Math.pow(facteur, i));

    const phrase = `Terme 1 : {{${t[0]}}}.\nTerme 2 : ${t[0]} × ${facteur} = {{${t[1]}}}.\nTerme 3 : ${t[1]} × ${facteur} = {{${t[2]}}}.\nTerme 4 : ${t[2]} × ${facteur} = {{${t[3]}}}.\nTerme 5 : ${t[3]} × ${facteur} = {{${t[4]}}}.\nPour passer d'un terme au suivant, on multiplie par {{${facteur}}}.\nLe ${rang}e terme est {{${terme}}}.`;

    const texte = `<p>Voici le début d'une suite : ${t[0]} ; ${t[1]} ; ${t[2]} ; … Complète le raisonnement pour trouver le ${rang}<sup>e</sup> terme.</p>`;

    const payload = makeTexteTrou(phrase, texte, { ...opts(L_MULTIPLI), mode: 'saisie', strict: false });
    // Calculatrice AUTORISÉE (revue 2026-08-02) : la dernière étape enchaîne
    // jusqu'à 4 multiplications non guidées (ex. 208 → 3328 au rang 9).
    payload.contenu = { ...payload.contenu, calculatrice: true };
    payload._meta.generatorKey = 'suite_multiplicative';
    return payload;
};


// ═══════════════════════════════════════════════════════════
// EXERCICE 3 — suite_logique (suites additives de décimaux)
// Carte entière transplantée du gen 609 (revue 2026-08-27, choix prof) :
// QCM du terme suivant, saisie du terme suivant, texte à trous (2 termes
// masqués). Les originaux restent régénérables dans le 609 pour la banque.
// ═══════════════════════════════════════════════════════════

const L_SUITE_DEC = 'Compléter une suite logique de décimaux';

// Helpers décimaux (scaled = millièmes), autonomes pour ce gen.
const toScaledDec = (str) => Math.round(parseFloat(String(str).replace(',', '.')) * 1000);
const formatDecFixed638 = (scaled, decimales) => {
    const factor = Math.pow(10, 3 - decimales);
    const rounded = Math.round(scaled / factor);
    const base = Math.pow(10, decimales);
    const ent = Math.floor(rounded / base);
    const dec = rounded % base;
    if (decimales === 0) return String(ent);
    return `${ent},${String(dec).padStart(decimales, '0')}`;
};

// Pool curé (u0, pas en millièmes) + options de pas pour le tirage dynamique.
const SUITE_DEC_POOL = [
    { u0: '0,676', pas: 1,   nbDec: 3 },
    { u0: '1,2',   pas: 200, nbDec: 1 },
    { u0: '3,25',  pas: 250, nbDec: 2 },
    { u0: '0,1',   pas: 100, nbDec: 1 },
    { u0: '5,5',   pas: 500, nbDec: 1 },
    { u0: '2,15',  pas: 10,  nbDec: 2 },
    { u0: '10,3',  pas: 300, nbDec: 1 },
    { u0: '4,125', pas: 5,   nbDec: 3 },
];
const SUITE_DEC_PAS = [
    { pas: 1, nbDec: 3 }, { pas: 5, nbDec: 3 }, { pas: 10, nbDec: 2 },
    { pas: 20, nbDec: 2 }, { pas: 25, nbDec: 3 }, { pas: 50, nbDec: 2 },
    { pas: 100, nbDec: 1 }, { pas: 200, nbDec: 1 }, { pas: 250, nbDec: 2 },
    { pas: 300, nbDec: 1 }, { pas: 400, nbDec: 1 }, { pas: 500, nbDec: 1 },
];

const buildSuiteDec = (count) => {
    // 30 % pool curé / 70 % dynamique (u0 multiple du pas, plage [0,1 ; 20])
    let cfg;
    if (Math.random() < 0.3) {
        cfg = randomChoice(SUITE_DEC_POOL);
    } else {
        const opt = randomChoice(SUITE_DEC_PAS);
        const k = randInt(Math.max(1, Math.ceil(100 / opt.pas)), Math.floor(20000 / opt.pas));
        cfg = { u0: formatDecFixed638(k * opt.pas, opt.nbDec), pas: opt.pas, nbDec: opt.nbDec };
    }
    const u0S = toScaledDec(cfg.u0);
    const termes = [];
    for (let i = 0; i < count; i++) termes.push(formatDecFixed638(u0S + i * cfg.pas, cfg.nbDec));
    const next = formatDecFixed638(u0S + count * cfg.pas, cfg.nbDec);
    return { termes, next, cfg };
};

// Anti-répétition immédiate (recette standard).
let derniereSignatureSuiteDecQcm638 = null;

const genSuiteDecQcm = () => {
    let payload = construireSuiteDecQcm();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureSuiteDecQcm638; t++) {
        payload = construireSuiteDecQcm();
    }
    derniereSignatureSuiteDecQcm638 = payload.texte;
    return payload;
};

const construireSuiteDecQcm = () => {
    const s = buildSuiteDec(4);
    const correct = s.next;
    const cS = toScaledDec(correct);
    // Distracteurs dédoublonnés PAR VALEUR (hérité de la revue 609 du
    // 2026-08-19) : pas ×10, pas inversé, saut d'un terme, saut de deux pas.
    const candidats = [
        formatDecFixed638(toScaledDec(s.termes[3]) + s.cfg.pas * 10, s.cfg.nbDec),
        formatDecFixed638(toScaledDec(s.termes[3]) - s.cfg.pas, s.cfg.nbDec),
        formatDecFixed638(cS + s.cfg.pas, s.cfg.nbDec),
        formatDecFixed638(cS + 2 * s.cfg.pas, s.cfg.nbDec),
    ];
    const vus = new Set([cS]);
    const dist = [];
    candidats.forEach((d) => {
        const v = toScaledDec(d);
        if (dist.length >= 3 || vus.has(v) || v <= 0) return;
        vus.add(v);
        dist.push(d);
    });
    while (dist.length < 3) {
        const alt = formatDecFixed638(cS + randInt(3, 20) * s.cfg.pas, s.cfg.nbDec);
        const v = toScaledDec(alt);
        if (!vus.has(v) && v > 0) { vus.add(v); dist.push(alt); }
    }
    const texte = `<p>Complète la suite logique. Quel est le nombre qui suit ?</p><p><strong>${s.termes.join(' ; ')} ; …</strong></p>`;
    return makeQcm([correct, ...dist], correct, texte, opts(L_SUITE_DEC));
};

// Anti-répétition immédiate (recette standard).
let derniereSignatureSuiteDecTxt638 = null;

const genSuiteDecTexte = () => {
    let payload = construireSuiteDecTexte();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureSuiteDecTxt638; t++) {
        payload = construireSuiteDecTexte();
    }
    derniereSignatureSuiteDecTxt638 = payload.texte;
    return payload;
};

const construireSuiteDecTexte = () => {
    const s = buildSuiteDec(4);
    const texte = `<p>Complète la suite logique :</p><p><strong>${s.termes.join(' ; ')} ; …</strong></p><p>Donne le nombre qui suit.</p>`;
    return makeSaisie(s.next, texte, opts(L_SUITE_DEC));
};

// Anti-répétition immédiate (recette standard).
let derniereSignatureSuiteDec638 = null;

const genSuiteDecTexteTrou = () => {
    let payload = construireSuiteDecTexteTrou();
    for (let t = 0; t < 8 && payload.contenu.phrase === derniereSignatureSuiteDec638; t++) {
        payload = construireSuiteDecTexteTrou();
    }
    derniereSignatureSuiteDec638 = payload.contenu.phrase;
    return payload;
};

const construireSuiteDecTexteTrou = () => {
    const s = buildSuiteDec(5);
    // Masquer 2 termes (positions 2 et 4)
    const phrase = [
        s.termes[0], s.termes[1], `{{${s.termes[2]}}}`, s.termes[3], `{{${s.termes[4]}}}`,
    ].join(' ; ');
    const texte = '<p>Complète la suite logique en tapant les nombres manquants.</p>';
    return makeTexteTrou(phrase, texte, {
        ...opts(L_SUITE_DEC),
        mode: 'saisie',
        distracteurs: [],
        strict: false,
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    suite_additive_qcm:              genAdditiveQcm,
    suite_additive_saisie:           genAdditiveSaisie,
    suite_additive_texte_trou:       genAdditiveTexteTrou,
    suite_multiplicative_qcm:        genMultiQcm,
    suite_multiplicative_saisie:     genMultiSaisie,
    suite_multiplicative_texte_trou: genMultiTexteTrou,

    // Carte « suite logique de décimaux » transplantée du gen 609 (revue 2026-08-27).
    suite_logique_qcm_unique:        genSuiteDecQcm,
    suite_logique_texte:             genSuiteDecTexte,
    suite_logique_texte_trou_saisie: genSuiteDecTexteTrou,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag', texte_trou: 'Texte à trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
    qcm: 'QCM', saisie: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'suite_additive',       label: L_ADDITIVE, formatsList: buildFormats('suite_additive') },
    // Revue 2026-08-27 (choix prof) : la carte multiplicative est TRANSPLANTÉE
    // au gen 689 (« répéter une instruction sur un nombre », ré-habillage en
    // langage d'instructions) ; masquée ici, générateurs conservés pour la banque.
    // { id: 'suite_multiplicative', label: L_MULTIPLI,  formatsList: buildFormats('suite_multiplicative') },
    // Carte transplantée du gen 609 (revue 2026-08-27).
    { id: 'suite_logique',        label: L_SUITE_DEC, formatsList: buildFormats('suite_logique') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    CTX_ADDITIF,
    genAdditiveData, genMultiData,
    genAdditiveQcm, genAdditiveSaisie, genAdditiveTexteTrou,
    genMultiQcm, genMultiSaisie, genMultiTexteTrou,
    genSuiteDecQcm, genSuiteDecTexte, genSuiteDecTexteTrou,
};
